import React from 'react';
import PropTypes from 'prop-types';
import block from 'bem-cn-lite';
import {Button} from '@yandex-data-ui/common';
import {Howl} from 'howler';
import fileSaver from 'file-saver';
import Recorder from '@yandex-data-ui/audio-recorder';
import '@doc-tools/transform/dist/js/yfm';

import {compose} from 'recompose';
import withLocale from 'hoc/withLocale';
import withTranslation from 'hoc/withTranslation';
import withSpeechkitConfig from 'hoc/withSpeechkitConfig';

import SpeechKitTTSForm, {
    getInitialValuesRu,
    getInitialValuesEn,
} from 'components/SpeechKitTTS/SpeechKitTTSForm/SpeechKitTTSForm';
import Highlight from 'components/Highlight/Highlight';
import {sendTextToSpeech} from 'components/SpeechKitTTS/api';
import {base64ArrayBuffer, isSafari} from 'components/SpeechKitTTS/utils';
import DownloadButton from 'components/Buttons/DownloadButton/DownloadButton';
import CollapsibleList from 'components/CollapsibleList/CollapsibleList';
import SpeechkitConstants from '../../../common/speechkit/constants';

import 'styles/yfm.scss';
import './SpeechKitTTS.scss';

const b = block('SpeechKitTTS');

class SpeechKitTTS extends React.Component {
    static propTypes = {
        locale: PropTypes.object.isRequired,
        i18nK: PropTypes.func,
        isConstructorVersion: PropTypes.bool,
        speechkitConfig: PropTypes.object,
    };

    constructor(props) {
        super(props);
        const {i18nK} = props;

        this.state = {
            form:
                props.locale.lang === 'ru' ? getInitialValuesRu(i18nK) : getInitialValuesEn(i18nK),
            base64: null,
            buffer: null,
            playing: false,
            loading: false,
            showCode: false,
            messageFilled: true,
            error: false,
        };
    }

    getMime = () => {
        const {format} = Recorder.getDefaultFormat();

        if (format === 'opus') {
            return {
                mime: 'audio/ogg',
                type: 'ogg',
            };
        } else {
            return {
                mime: 'audio/wav',
                type: 'wav',
            };
        }
    };

    onLoad = async (form) => {
        try {
            this.setState({loading: true, error: false});

            const {format} = Recorder.getDefaultFormat();

            const {data} = await sendTextToSpeech({
                ...form,
                format:
                    format === 'opus'
                        ? SpeechkitConstants.Format.OGGOPUS
                        : SpeechkitConstants.Format.LPCM,
            });

            let buffer = data;

            if (format !== 'opus') {
                buffer = Recorder.addRiffWavHeader(data);
            }

            const base64 = base64ArrayBuffer(buffer);

            this.player = new Howl({
                src: `data:${this.getMime().mime};base64,${base64}`,
            });

            this.player.on('end', this.onEnd);

            this.setState({buffer, base64}, () => {
                if (!isSafari()) {
                    this.onPlay();
                }
            });
        } catch {
            this.setState({error: true});
        } finally {
            this.setState({loading: false});
        }
    };

    onPlay = () => {
        if (this.player) {
            this.setState({playing: true});
            this.player.play();
        }
    };

    onPause = () => {
        if (this.player) {
            this.setState({playing: false});
            this.player.pause();
        }
    };

    onEnd = () => {
        this.setState({playing: false});
    };

    onButtonClick = async (form) => {
        const {base64, playing, loading} = this.state;

        if (loading === true) {
            return;
        }

        if (base64 === null) {
            this.onLoad(form);
        } else if (playing === false) {
            this.onPlay();
        } else {
            this.onPause();
        }
    };

    onFormChange = (form) => {
        this.setState({
            loading: false,
            base64: null,
            buffer: null,
            messageFilled: Boolean(form.message),
            form,
        });
    };

    downloadSample = async () => {
        const {buffer} = this.state;
        const {type} = this.getMime();
        fileSaver.saveAs(new Blob([buffer]), 'tts.' + type);
    };

    renderAdvice = () => {
        const {i18nK, speechkitConfig} = this.props;
        return (
            <div className={b('advice')}>
                {i18nK('tts-advice', {limit: speechkitConfig.ttsMessageLimit})}
            </div>
        );
    };

    renderForm = () => {
        return (
            <div className={b('form')}>
                <SpeechKitTTSForm
                    onSubmit={this.onButtonClick}
                    onChange={this.onFormChange}
                    ref={(ref) => {
                        this.form = ref;
                    }}
                />
            </div>
        );
    };

    onPlayPauseClick = () => {
        const {playing} = this.state;

        if (playing) {
            this.onPause();
        } else {
            this.form.submit();
        }
    };

    renderActions = () => {
        const {i18nK} = this.props;
        const {base64, playing, loading, messageFilled, error} = this.state;
        let buttonText = '';

        if (playing) {
            buttonText = i18nK('tts-button_pause');
        } else if (base64) {
            buttonText = i18nK('tts-button_play');
        } else {
            buttonText = i18nK('tts-button_load');
        }

        return (
            <div className={b('actions')}>
                <div className={b('buttons')}>
                    <Button
                        type="submit"
                        size="l"
                        view="action"
                        onClick={this.onPlayPauseClick}
                        loading={loading}
                        disabled={!messageFilled}
                        className={b('button')}
                    >
                        {buttonText}
                    </Button>
                    <DownloadButton
                        size="l"
                        view="action"
                        disabled={!base64}
                        onClick={this.downloadSample}
                        iconSize={18}
                        className={b('button')}
                    />
                </div>
                {error && <div className={b('error')}>{i18nK('tts-action_error')}</div>}
            </div>
        );
    };

    renderBottom = () => {
        const {i18nK} = this.props;
        const {form} = this.state;
        const json = form
            ? {
                  text: form.message,
                  lang: form.language,
                  speed: form.speed,
                  voice: form.voice,
                  emotion: form.emotion,
              }
            : {
                  text: 'string',
                  lang: 'string',
                  voice: 'string',
                  emotion: 'string',
                  speed: 'number',
              };

        return (
            <CollapsibleList arrowPosition="right">
                <section
                    title={i18nK('tts-show_code-button')}
                    extra={this.renderActions()}
                    className={'yfm'}
                >
                    <h2>{i18nK('tts-show_code-url_title')}</h2>
                    <pre>
                        <code>{i18nK('tts-show_code-url')}</code>
                    </pre>
                    <h2>{i18nK('tts-show_code-params_title')}</h2>
                    <Highlight languages={['json']}>{JSON.stringify(json, null, 4)}</Highlight>
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={i18nK('tts-show_code-link_url')}
                    >
                        {i18nK('tts-show_code-link')}
                    </a>
                </section>
            </CollapsibleList>
        );
    };

    render() {
        const {isConstructorVersion} = this.props;

        return (
            <div className={b({constructor: isConstructorVersion})}>
                <div className={b('main')}>
                    <div className={b('top')}>
                        {this.renderForm()}
                        {this.renderAdvice()}
                    </div>
                    <div className={b('bottom')}>{this.renderBottom()}</div>
                </div>
            </div>
        );
    }
}
export default compose(
    withLocale,
    withSpeechkitConfig,
    withTranslation('speechkit-demo'),
)(SpeechKitTTS);
